import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useOrganization } from '../../hooks/useOrganization';
import { organizationService } from '../../services';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { Eye, EyeOff, Users } from 'lucide-react';

// Individual OKR visibility options
type IndividualOkrVisibility = 'private' | 'manager_visible' | 'all_visible';

const visibilityOptions: Array<{
  value: IndividualOkrVisibility;
  label: string;
  description: string;
  icon: React.ReactNode;
}> = [
  {
    value: 'private',
    label: 'Private',
    description: 'Only the OKR owner can see their Individual OKRs. Most restrictive setting for privacy.',
    icon: <EyeOff className="w-5 h-5" />,
  },
  {
    value: 'manager_visible',
    label: 'Manager Visible',
    description: 'Individual OKRs are visible to the owner and their direct manager only.',
    icon: <Eye className="w-5 h-5" />,
  },
  {
    value: 'all_visible',
    label: 'Organization Visible',
    description: 'All employees in the organization can see Individual OKRs. Maximum transparency.',
    icon: <Users className="w-5 h-5" />,
  },
];

export const OrganizationSettings: React.FC = () => {
  const { user } = useAuth();
  const { data: organization, isLoading: orgLoading, refetch } = useOrganization();

  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVisibilityLoading, setIsVisibilityLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [formData, setFormData] = useState({
    name: organization?.name || '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Get current Individual OKR visibility from organization settings
  const currentVisibility: IndividualOkrVisibility =
    ((organization?.settings as Record<string, unknown>)?.individualOkrVisibility as IndividualOkrVisibility) ||
    'private';

  const [selectedVisibility, setSelectedVisibility] = useState<IndividualOkrVisibility>(currentVisibility);

  React.useEffect(() => {
    if (organization) {
      setFormData({
        name: organization.name,
      });
      // Update visibility when organization data is loaded
      const visibility = ((organization.settings as Record<string, unknown>)?.individualOkrVisibility as IndividualOkrVisibility) || 'private';
      setSelectedVisibility(visibility);
    }
  }, [organization]);

  if (!user) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (user.role !== 'account_admin') {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <Card className="p-8 max-w-md">
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-stone-900 mb-4" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Access Denied</h2>
            <p className="text-stone-600 mb-6">
              Only administrators can manage organization settings.
            </p>
            <Button variant="primary" onClick={() => window.history.back()}>
              Go Back
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'Organization name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    setErrorMessage('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      await organizationService.updateOrganization({ name: formData.name });
      await refetch();
      setSuccessMessage('Organization settings updated successfully!');
      setIsEditing(false);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Update organization error:', error);
      const err = error as { response?: { data?: { message?: string } } };
      setErrorMessage(
        err.response?.data?.message ||
        'Failed to update organization settings. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelEdit = () => {
    if (organization) {
      setFormData({
        name: organization.name,
      });
    }
    setErrors({});
    setIsEditing(false);
    setErrorMessage('');
  };

  const handleVisibilityChange = async (visibility: IndividualOkrVisibility) => {
    if (visibility === selectedVisibility) return;

    setIsVisibilityLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      await organizationService.updateSettings({
        individualOkrVisibility: visibility,
      });
      setSelectedVisibility(visibility);
      await refetch();
      setSuccessMessage('Individual OKR visibility updated successfully!');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Update visibility error:', error);
      const err = error as { response?: { data?: { message?: string } } };
      setErrorMessage(
        err.response?.data?.message ||
        'Failed to update visibility setting. Please try again.'
      );
    } finally {
      setIsVisibilityLoading(false);
    }
  };

  if (orgLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-3xl font-semibold text-stone-900" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Organization Settings</h1>
          <p className="mt-1 text-sm text-stone-500">
            Manage your organization's information and preferences
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {successMessage && (
          <div className="mb-6 bg-success-50 border border-success-200 text-success-700 px-4 py-3 rounded-lg">
            {successMessage}
          </div>
        )}

        {errorMessage && (
          <div className="mb-6 bg-danger-50 border border-danger-200 text-danger-700 px-4 py-3 rounded-lg">
            {errorMessage}
          </div>
        )}

        {/* Organization Information */}
        <Card className="p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-stone-900">Organization Information</h2>
            {!isEditing && (
              <Button variant="secondary" onClick={() => setIsEditing(true)}>
                Edit Settings
              </Button>
            )}
          </div>

          <form onSubmit={handleSubmit}>
            <div className="space-y-6 mb-6">
              <Input
                label="Organization Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={errors.name}
                disabled={!isEditing}
                placeholder="Acme Corporation"
                required
              />
            </div>

            {isEditing && (
              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleCancelEdit}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="flex items-center">
                      <LoadingSpinner size="sm" className="mr-2" />
                      Saving...
                    </span>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </div>
            )}
          </form>
        </Card>

        {/* Individual OKR Privacy Settings */}
        <Card className="p-6 mb-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-stone-900">Individual OKR Privacy</h2>
            <p className="mt-1 text-sm text-stone-500">
              Control who can see Individual-type OKRs within your organization.
              Company, Department, and Team OKRs are always visible to all employees.
            </p>
          </div>

          <div className="space-y-3">
            {visibilityOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleVisibilityChange(option.value)}
                disabled={isVisibilityLoading}
                className={`w-full p-4 rounded-lg border-2 transition-all text-left flex items-start gap-4 ${
                  selectedVisibility === option.value
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-stone-200 hover:border-stone-300 hover:bg-stone-50'
                } ${isVisibilityLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div
                  className={`p-2 rounded-lg ${
                    selectedVisibility === option.value
                      ? 'bg-primary-100 text-primary-600'
                      : 'bg-stone-100 text-stone-500'
                  }`}
                >
                  {option.icon}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span
                      className={`font-medium ${
                        selectedVisibility === option.value
                          ? 'text-primary-700'
                          : 'text-stone-900'
                      }`}
                    >
                      {option.label}
                    </span>
                    {selectedVisibility === option.value && (
                      <span className="text-xs bg-primary-200 text-primary-700 px-2 py-0.5 rounded">
                        Current
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-stone-500">{option.description}</p>
                </div>
                {isVisibilityLoading && selectedVisibility !== option.value && (
                  <LoadingSpinner size="sm" />
                )}
              </button>
            ))}
          </div>

          <div className="mt-4 p-3 bg-stone-50 rounded-lg">
            <p className="text-xs text-stone-600">
              <strong>Note:</strong> Account Admins and Managers can always see all OKRs regardless of this setting.
              This setting only affects visibility for Employees and Viewers.
            </p>
          </div>
        </Card>

        {/* Organization Stats */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-stone-900 mb-6">Organization Stats</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-primary-50 rounded-lg">
              <p className="text-sm text-primary-600 font-medium">Created</p>
              <p className="text-lg font-bold text-primary-900">
                {organization?.createdAt
                  ? new Date(organization.createdAt).toLocaleDateString()
                  : '-'}
              </p>
            </div>
            <div className="text-center p-4 bg-success-50 rounded-lg">
              <p className="text-sm text-success-600 font-medium">Status</p>
              <p className="text-lg font-bold text-success-900 capitalize">
                {organization?.status || 'Active'}
              </p>
            </div>
            <div className="text-center p-4 bg-stone-100 rounded-lg">
              <p className="text-sm text-stone-600 font-medium">Plan</p>
              <p className="text-lg font-bold text-stone-900 capitalize">
                {organization?.plan || 'Trial'}
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
